import { NativeAuthCallback } from './pages/NativeAuthCallback';
import { AppDownloadBanner } from './components/AppDownloadBanner';
import { PageMetadata } from './components/PageMetadata';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@primer/react';
import {
  Home,
  Compass,
  Dumbbell,
  Warehouse,
  Building2,
  Repeat2,
  MessageSquare,
  MessagesSquare,
  Bell,
  Settings,
  ShieldCheck,
  Search,
  Plus,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  X,
  ChevronRight,
  Flame,
  Play,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Info,
  UserRound,
  Users,
} from 'lucide-react';
import { useApp } from './state/AppContext';
import { readSidebarCollapsed, writeSidebarCollapsed } from './state/sidebarPreference';
import { supabase } from './state/client';
import {
  accountDeletionMessagePolicy,
  useAccountMessageErasure,
} from './state/accountDeletionPolicy';
import { Avatar, Empty, Logo, Modal } from './components/UI';
import { AppSkeleton, PageSkeleton, Skeleton } from './components/Skeleton';
import { GlobalSearchScaffold } from './components/ProfileSkeletons';
import { ChatFab } from './components/ChatFab';
import { NotificationMenu } from './components/NotificationMenu';
import { GymBackdropProvider } from './components/GymCover';
import { AuthPage, AuthCallback } from './pages/Auth';
import { normalizeProfileName, profileNameFromPath, profilePath } from '@garagegymbros/domain';
import { topbarPlaceLabel } from './state/topbarPlace';
import { isStaffRole } from '@garagegymbros/contracts';
import { androidNavigationEvent, isAndroidApp } from './state/androidBridge';
import type { ChatToastTarget } from './state/toasts';
const community = (name: string) =>
  lazy(() =>
    import('./community/CommunityPages').then((m) => ({
      default: m[name as keyof typeof m] as React.ComponentType,
    })),
  );
const HomePage = community('HomePage'),
  StoriesPage = community('StoriesPage'),
  DiscoverPage = community('DiscoverPage'),
  ForumPage = community('ForumPage'),
  TradePage = community('TradePage'),
  MessagesPage = community('MessagesPage'),
  NotificationsPage = community('NotificationsPage');
const CatalogPage = lazy(() => import('./pages/Catalog').then((m) => ({ default: m.CatalogPage }))),
  ProductPage = lazy(() => import('./pages/Catalog').then((m) => ({ default: m.ProductPage }))),
  GymPage = lazy(() => import('./pages/Gym').then((m) => ({ default: m.GymPage }))),
  MyProfilePage = lazy(() => import('./pages/Gym').then((m) => ({ default: m.MyProfilePage }))),
  ProfilePage = lazy(() => import('./pages/Gym').then((m) => ({ default: m.ProfilePage }))),
  SettingsPage = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage }))),
  FollowersPage = lazy(() =>
    import('./pages/Followers').then((m) => ({ default: m.FollowersPage })),
  ),
  AdminPage = lazy(() => import('./pages/Admin').then((m) => ({ default: m.AdminPage })));
const CompaniesPage = lazy(() =>
    import('./pages/Companies').then((m) => ({ default: m.CompaniesPage })),
  ),
  CompanyPage = lazy(() => import('./pages/Companies').then((m) => ({ default: m.CompanyPage })));
const ManufacturerEquipmentPage = lazy(() =>
  import('./pages/ManufacturerEquipment').then((m) => ({ default: m.ManufacturerEquipmentPage })),
);
const GlobalSearch = lazy(() =>
  import('./components/GlobalSearch').then((m) => ({ default: m.GlobalSearch })),
);
type NavEntry = {
  path: string;
  label: string;
  icon: typeof Home;
  children?: { path: string; label: string; icon: typeof Home }[];
};
const nav: NavEntry[] = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/my-gym', label: 'My Gym', icon: Warehouse },
  { path: '/my-profile', label: 'My Profile', icon: UserRound },
  { path: '/flexes', label: 'Flexes', icon: Play },
  { path: '/discover', label: 'Discover', icon: Compass },
  { path: '/account/followers', label: 'Followers', icon: Users },
  {
    path: '/catalog',
    label: 'Equipment',
    icon: Dumbbell,
    children: [
      { path: '/companies', label: 'Companies', icon: Building2 },
      { path: '/trade', label: 'Trade', icon: Repeat2 },
    ],
  },
  { path: '/forum', label: 'Forum', icon: MessagesSquare },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/notifications', label: 'Notifications', icon: Bell },
];
const androidDestinations = new Set(['/home', '/catalog', '/my-gym', '/messages', '/my-profile']);

export default function App() {
  const navigate = useNavigate();
  const [chatRequest, setChatRequest] = useState<ChatToastTarget>();
  useEffect(() => {
    if (!isAndroidApp) return;
    function navigateFromDock(event: Event) {
      const path = (event as CustomEvent<unknown>).detail;
      if (typeof path !== 'string' || !androidDestinations.has(path)) return;
      event.preventDefault();
      if (window.location.pathname !== path || window.location.search || window.location.hash) {
        void navigate(path);
      } else {
        window.scrollTo({ top: 0 });
      }
    }
    window.addEventListener(androidNavigationEvent, navigateFromDock);
    return () => window.removeEventListener(androidNavigationEvent, navigateFromDock);
  }, [navigate]);
  return (
    <>
      <PageMetadata />
      {!isAndroidApp && <AppDownloadBanner />}
      <AppPages />
      {!isAndroidApp && <ChatFab request={chatRequest} />}
      <Toasts openChat={setChatRequest} />
    </>
  );
}
function AppPages() {
  const { mode, session, loading, accountReady, error, reload, currentUser, state } = useApp(),
    location = useLocation();
  if (import.meta.env.DEV && location.pathname === '/preview/member-photos')
    return <Navigate to="/admin?tab=photos" replace />;
  const publicPath =
    location.pathname === '/' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname.startsWith('/policies');
  if (publicPath)
    return (
      <>
        <Routes>
          <Route path="/" element={<AuthPage />} />
          <Route path="/auth/ios/callback" element={<NativeAuthCallback />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/*" element={<AuthPage />} />
          <Route path="/policies/:kind" element={<PolicyPage />} />
        </Routes>
      </>
    );
  const publicProfile =
    profileNameFromPath(location.pathname) !== null ||
    /^\/profile\/[^/]+\/?$/.test(location.pathname);
  const publicCompany = /^\/companies(?:\/[^/]+)?\/?$/.test(location.pathname);
  if (loading)
    return (
      <AppSkeleton
        pathname={location.pathname}
        publicProfile={(publicProfile || publicCompany) && !session}
        catalogView={state.catalog_view}
        sampleGallery={Boolean(currentUser.sample_media)}
        search={location.search}
      />
    );
  if (mode === 'live' && !accountReady && error)
    return (
      <div className="app-load-error">
        <Logo />
        <Empty
          title="The garage couldn’t load"
          action={<Button onClick={() => void reload()}>Try again</Button>}
        >
          <span role="alert">{error}</span>
        </Empty>
      </div>
    );
  // Each viewer gets a fresh profile instance; logout cannot retain follower-only content.
  const profilePage = (
    <ProfilePage key={session?.user.id ?? (mode === 'demo' ? currentUser.id : 'guest')} />
  );
  const companyPage = (
    <CompanyPage
      key={`${session?.user.id ?? (mode === 'demo' ? currentUser.id : 'guest')}:${location.pathname}`}
    />
  );
  if (mode === 'live' && !session && publicProfile)
    return (
      <PublicProfileShell>
        <Suspense
          fallback={
            <PageSkeleton
              pathname={location.pathname}
              catalogView={state.catalog_view}
              sampleGallery={Boolean(currentUser.sample_media)}
              search={location.search}
            />
          }
        >
          <Routes>
            <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/companies/:slug" element={companyPage} />
            <Route path="/profile/:id" element={profilePage} />
            <Route path="/:username" element={profilePage} />
          </Routes>
        </Suspense>
      </PublicProfileShell>
    );
  if (mode === 'unconfigured' || (mode === 'live' && !session))
    return (
      <Navigate
        to={`/auth/signin?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
        replace
      />
    );
  return (
    <Shell>
      <Suspense
        fallback={
          <PageSkeleton
            pathname={location.pathname}
            catalogView={state.catalog_view}
            sampleGallery={Boolean(currentUser.sample_media)}
            search={location.search}
          />
        }
      >
        <Routes>
          <Route path="/home/*" element={<HomePage />} />
          <Route path="/flexes/*" element={<StoriesPage />} />
          <Route path="/stories/*" element={<StoriesPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/account/followers" element={<FollowersPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/catalog/:slug" element={<ProductPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:slug" element={companyPage} />
          <Route
            path="/companies/:slug/equipment"
            element={
              <ManufacturerEquipmentPage
                key={`${session?.user.id ?? currentUser.id}:${location.pathname}`}
              />
            }
          />
          <Route path="/my-gym" element={<GymPage />} />
          <Route path="/my-profile" element={<MyProfilePage />} />
          <Route path="/profile/:id?" element={profilePage} />
          <Route path="/trade/*" element={<TradePage />} />
          <Route path="/forum/*" element={<ForumPage />} />
          <Route path="/messages/*" element={<MessagesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin/*" element={<AdminPage />} />
          <Route path="/:username" element={profilePage} />
          <Route
            path="*"
            element={
              <Empty
                title="This page isn’t in the garage"
                action={<a href="/home">Back to Home</a>}
              >
                Check the address or head back to your community.
              </Empty>
            }
          />
        </Routes>
      </Suspense>
    </Shell>
  );
}
function useScrolledTopbar(offset = 24) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setScrolled(false);
  }, [location.pathname]);
  useEffect(() => {
    const sync = () => setScrolled(window.scrollY > offset);
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    return () => window.removeEventListener('scroll', sync);
  }, [offset]);
  return scrolled;
}

function PublicProfileShell({ children }: { children: ReactNode }) {
  const location = useLocation(),
    [search, setSearch] = useState(''),
    [searchOpen, setSearchOpen] = useState(false);
  const topbarScrolled = useScrolledTopbar();
  return (
    <div className="public-profile-shell">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className={`topbar public-profile-header${topbarScrolled ? ' is-scrolled' : ''}`}>
        <NavLink to="/" className="brand-link public-profile-brand" aria-label="GarageGymBros home">
          <Logo />
        </NavLink>
        <form
          className="header-search site-search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearchOpen(true);
          }}
        >
          <Search size={14} aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search the community"
            placeholder="Search the community…"
          />
          <kbd>↵</kbd>
        </form>
        <div className="public-profile-auth">
          <NavLink to={`/auth/signin?next=${encodeURIComponent(location.pathname)}`}>Login</NavLink>
          <NavLink className="public-profile-join" to="/auth/register">
            Sign Up
          </NavLink>
        </div>
      </header>
      <main id="main-content">{children}</main>
      {searchOpen && (
        <Suspense
          fallback={
            <Modal title="Search the garage" onClose={() => setSearchOpen(false)} wide>
              <GlobalSearchScaffold>
                <Skeleton variant="search" label="Loading search…" count={3} />
              </GlobalSearchScaffold>
            </Modal>
          }
        >
          <GlobalSearch query={search} onClose={() => setSearchOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}
function Shell({ children }: { children: ReactNode }) {
  const { state, currentUser, mode, session, navigate, error, reload, notify } = useApp(),
    location = useLocation(),
    [mobile, setMobile] = useState(false),
    [collapsed, setCollapsed] = useState(readSidebarCollapsed),
    [search, setSearch] = useState(''),
    [searchOpen, setSearchOpen] = useState(false),
    [accountOpen, setAccountOpen] = useState(false),
    [notificationsOpen, setNotificationsOpen] = useState(false),
    [openNavGroups, setOpenNavGroups] = useState<Record<string, boolean>>({});
  const topbarScrolled = useScrolledTopbar();
  const accountRef = useRef<HTMLDivElement>(null);
  const rail = collapsed && !mobile;
  const canAdmin = isStaffRole(state.demo_role);
  function toggleSidebar() {
    setCollapsed((current) => {
      const next = !current;
      writeSidebarCollapsed(next);
      return next;
    });
  }
  async function signOut() {
    setAccountOpen(false);
    if (mode === 'demo') {
      window.location.href = '/?demo=0';
      return;
    }
    if (!supabase) {
      navigate('/auth/signin');
      return;
    }
    // Other devices stay signed in; Settings provides an explicit sign-out-everywhere action.
    const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
    if (signOutError) {
      notify(signOutError.message, 'error');
      return;
    }
    navigate('/auth/signin');
  }
  const unread = state.notifications.filter((n) => !n.read).length;
  const followerCount = state.follows.filter(
    (follow) =>
      follow.following_id === currentUser.id &&
      follow.status === 'accepted' &&
      !state.blocks.includes(follow.follower_id),
  ).length;
  const ownProfilePath = normalizeProfileName(currentUser.username)
    ? profilePath(currentUser.username)
    : '/settings?tab=profile';
  const placeLabel = topbarPlaceLabel(currentUser.gym_name);
  useEffect(() => {
    setMobile(false);
    setAccountOpen(false);
    setNotificationsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  useEffect(() => {
    const path = location.pathname.replace(/^\/stories(?=\/|$)/, '/flexes');
    setOpenNavGroups((current) => {
      let changed = false;
      const next = { ...current };
      for (const item of nav) {
        if (!item.children?.length) continue;
        const routeOpen =
          path === item.path ||
          path.startsWith(`${item.path}/`) ||
          item.children.some((child) => path === child.path || path.startsWith(`${child.path}/`));
        if (routeOpen && next[item.path] !== true) {
          next[item.path] = true;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [location.pathname]);
  useEffect(() => {
    if (!accountOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountOpen(false);
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);
  const navPath = location.pathname.replace(/^\/stories(?=\/|$)/, '/flexes');
  function navCrumbs(path: string) {
    for (const item of nav) {
      if (item.children?.length) {
        const child = item.children.find(
          (entry) => path === entry.path || path.startsWith(`${entry.path}/`),
        );
        if (child) return [{ label: item.label, path: item.path }, { label: child.label }];
        if (path === item.path || path.startsWith(`${item.path}/`)) return [{ label: item.label }];
      } else if (path === item.path || path.startsWith(`${item.path}/`)) {
        return [{ label: item.label }];
      }
    }
    return [{ label: 'Your space' }];
  }
  const crumbs = navCrumbs(navPath);
  function navGroupOpen(item: NavEntry) {
    if (openNavGroups[item.path] !== undefined) return openNavGroups[item.path];
    return (
      navPath === item.path ||
      navPath.startsWith(`${item.path}/`) ||
      Boolean(
        item.children?.some(
          (child) => navPath === child.path || navPath.startsWith(`${child.path}/`),
        ),
      )
    );
  }
  function toggleNavGroup(path: string) {
    setOpenNavGroups((current) => {
      const item = nav.find((entry) => entry.path === path);
      const currentlyOpen =
        current[path] ??
        Boolean(
          item &&
          (navPath === item.path ||
            navPath.startsWith(`${item.path}/`) ||
            item.children?.some(
              (child) => navPath === child.path || navPath.startsWith(`${child.path}/`),
            )),
        );
      return { ...current, [path]: !currentlyOpen };
    });
  }
  const navLink = ({
    path,
    label,
    icon: Icon,
    nested = false,
  }: {
    path: string;
    label: string;
    icon: typeof Home;
    nested?: boolean;
  }) => (
    <NavLink
      key={path}
      to={path}
      title={
        rail ? (path === '/account/followers' ? `${label} (${followerCount})` : label) : undefined
      }
      className={({ isActive }) =>
        `nav-item${nested ? ' nav-item-nested' : ''}${
          isActive || (path === '/flexes' && navPath !== location.pathname) ? ' active' : ''
        }`
      }
    >
      <Icon size={nested ? 17 : 19} />
      <span>{path === '/account/followers' ? `${label} (${followerCount})` : label}</span>
      {path === '/notifications' && unread > 0 && <b className="nav-count">{unread}</b>}
      {path === '/messages' && state.conversations.some((c) => c.unread_count > 0) && (
        <i className="unread-dot" />
      )}
    </NavLink>
  );
  return (
    <GymBackdropProvider userId={currentUser.id}>
      <div className={`app-shell${collapsed ? ' sidebar-collapsed' : ''}`}>
        <a href="#main-content" className="skip-link">
          Skip to content
        </a>
        {mobile && <div className="sidebar-scrim" onClick={() => setMobile(false)} />}
        <aside
          id="app-sidebar"
          className={`sidebar elevation-1${mobile ? ' sidebar-open' : ''}${rail ? ' sidebar-collapsed' : ''}`}
          aria-label="Main navigation"
        >
          <div className="sidebar-header">
            <NavLink
              to="/home"
              className="brand-link"
              aria-label={rail ? 'GarageGymBros home' : undefined}
            >
              <Logo compact={rail} />
            </NavLink>
            <button
              type="button"
              className="icon-button sidebar-close"
              aria-label="Close navigation"
              onClick={() => setMobile(false)}
            >
              <X size={20} />
            </button>
          </div>
          <div className="sidebar-divider" />
          <nav>
            {nav.map((item) => {
              if (!item.children?.length) return navLink(item);
              const groupId = `nav-group-${item.path.slice(1).replace(/\//g, '-')}`;
              const open = !rail && navGroupOpen(item);
              const parentActive = navPath === item.path || navPath.startsWith(`${item.path}/`);
              const { icon: Icon } = item;
              return (
                <div key={item.path} className={`nav-group${open ? ' is-open' : ''}`}>
                  <div className={`nav-item nav-group-head${parentActive ? ' active' : ''}`}>
                    <NavLink
                      to={item.path}
                      className="nav-group-link"
                      title={rail ? item.label : undefined}
                    >
                      <Icon size={19} />
                      <span>{item.label}</span>
                    </NavLink>
                    {!rail && (
                      <button
                        type="button"
                        className="nav-group-toggle"
                        aria-expanded={open}
                        aria-controls={groupId}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label}`}
                        onClick={() => toggleNavGroup(item.path)}
                      >
                        <ChevronDown size={14} />
                      </button>
                    )}
                  </div>
                  {open && (
                    <div
                      id={groupId}
                      className="nav-group-children"
                      role="group"
                      aria-label={item.label}
                    >
                      {item.children.map((child) => navLink({ ...child, nested: true }))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="sidebar-bottom">
            <button
              type="button"
              className="icon-button sidebar-collapse"
              aria-expanded={!rail}
              aria-controls="app-sidebar"
              aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={toggleSidebar}
            >
              {rail ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
        </aside>
        <div className="app-main">
          <header className={`topbar${topbarScrolled ? ' is-scrolled' : ''}`}>
            <div className="topbar-location">
              <button
                className="icon-button mobile-menu"
                aria-label="Open navigation"
                onClick={() => setMobile(true)}
              >
                <Menu size={23} />
              </button>
              <span className="topbar-place" title={placeLabel}>
                {placeLabel}
              </span>
              {crumbs.map((crumb, index) => (
                <span className="topbar-crumb-group" key={`${crumb.label}:${index}`}>
                  <span className="topbar-slash">/</span>
                  {index < crumbs.length - 1 && crumb.path ? (
                    <NavLink to={crumb.path} className="topbar-crumb">
                      {crumb.label}
                    </NavLink>
                  ) : (
                    <strong className="topbar-page">{crumb.label}</strong>
                  )}
                </span>
              ))}
              <NavLink to="/home" className="topbar-logo" aria-label="GarageGymBros home">
                <Logo />
              </NavLink>
            </div>
            <form
              className="header-search site-search"
              onSubmit={(e) => {
                e.preventDefault();
                setSearchOpen(true);
              }}
            >
              <Search size={14} aria-hidden="true" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search the community"
                placeholder="Find your next great idea…"
              />
              <kbd>↵</kbd>
            </form>
            <div className="topbar-actions">
              <button
                className="icon-button mobile-search"
                aria-label="Search the community"
                onClick={() => setSearchOpen(true)}
              >
                <Search size={14} aria-hidden="true" />
              </button>
              <NotificationMenu
                open={notificationsOpen}
                onOpenChange={(open) => {
                  setNotificationsOpen(open);
                  if (open) setAccountOpen(false);
                }}
              />
              <div className="account-menu" ref={accountRef}>
                <button
                  type="button"
                  className="avatar-button"
                  aria-label="Account menu"
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  onClick={() => {
                    setAccountOpen((open) => !open);
                    setNotificationsOpen(false);
                  }}
                >
                  <Avatar member={currentUser} size={34} />
                </button>
                {accountOpen && (
                  <div className="account-menu-panel" role="menu" aria-label="Account">
                    <div className="account-menu-identity">
                      <strong>{currentUser.display_name}</strong>
                      <span>@{currentUser.username}</span>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setAccountOpen(false);
                        navigate(ownProfilePath);
                      }}
                    >
                      <UserRound size={16} aria-hidden="true" />
                      Profile
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setAccountOpen(false);
                        navigate('/settings');
                      }}
                    >
                      <Settings size={16} aria-hidden="true" />
                      Settings
                    </button>
                    {canAdmin && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setAccountOpen(false);
                          navigate('/admin');
                        }}
                      >
                        <ShieldCheck size={16} aria-hidden="true" />
                        Administration
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className="account-menu-signout"
                      onClick={() => void signOut()}
                    >
                      <LogOut size={16} aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>
          {mode === 'demo' && (
            <div className="demo-ribbon">
              <span>
                <i /> LOCAL DEMO
              </span>
              <p>Five fictional members. Your changes stay in this browser.</p>
              <button onClick={() => navigate('/settings')}>
                Demo controls <ChevronRight size={12} />
              </button>
              {supabase && <a href="/?demo=0">Exit preview</a>}
            </div>
          )}
          {session && !session.user.email_confirmed_at && (
            <div className="verification-banner">
              Verify your email to publish, review equipment, or message members.{' '}
              <button
                onClick={async () => {
                  if (supabase && session.user.email)
                    await supabase.auth.resend({ type: 'signup', email: session.user.email });
                }}
              >
                Resend verification
              </button>
            </div>
          )}
          {state.deactivated && (
            <div className="verification-banner">
              Your account is deactivated. Restore it in Privacy & data settings.
            </div>
          )}
          {error && (
            <div className="error-banner" role="alert">
              <AlertCircle size={17} />
              <span>{error}</span>
              <button onClick={() => void reload()}>Retry</button>
            </div>
          )}
          <main className="main-content" id="main-content" tabIndex={-1}>
            {children}
          </main>
          <footer className="app-footer">
            <span className="app-footer-brand">
              <Logo />
              <span>Built at home. Never alone.</span>
            </span>
            <div>
              <a href="/policies/community">Community</a>
              <a href="/policies/privacy">Privacy</a>
              <a href="/policies/terms">Terms</a>
              <a href="mailto:admin@garagegymbros.com">Support</a>
            </div>
          </footer>
        </div>
        {searchOpen && (
          <Suspense
            fallback={
              <Modal title="Search the garage" onClose={() => setSearchOpen(false)} wide>
                <GlobalSearchScaffold>
                  <Skeleton variant="search" label="Loading search…" count={3} />
                </GlobalSearchScaffold>
              </Modal>
            }
          >
            <GlobalSearch query={search} onClose={() => setSearchOpen(false)} />
          </Suspense>
        )}
      </div>
    </GymBackdropProvider>
  );
}
function Toasts({ openChat }: { openChat: (request: ChatToastTarget) => void }) {
  const { toasts, dismissToast, session } = useApp();
  const navigate = useNavigate();
  return (
    <div className="toast-stack" aria-live="polite">
      {toasts.map(({ id, kind, message, chat }) => {
        if (chat && chat.viewerId !== session?.user.id) return null;
        const Icon = kind === 'error' ? AlertCircle : kind === 'info' ? Info : CheckCircle2;
        return (
          <div
            key={id}
            className={`toast toast-${kind}${chat ? ' toast-message' : ''}`}
            role={kind === 'error' ? 'alert' : 'status'}
          >
            {chat ? (
              <button
                type="button"
                className="toast-open-message"
                onClick={() => {
                  dismissToast(id);
                  if (isAndroidApp) void navigate(`/messages/${chat.conversationId}`);
                  else openChat(chat);
                }}
              >
                <MessageSquare size={14} aria-hidden="true" />
                <span>
                  <strong>New message</strong>
                  <span>{message}</span>
                </span>
              </button>
            ) : (
              <>
                <Icon size={19} aria-hidden="true" />
                <span>{message}</span>
              </>
            )}
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismissToast(id)}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
function PolicyPage() {
  const path = useLocation().pathname;
  const kind = path.split('/').pop();
  const erasesMessages = useAccountMessageErasure(
    kind === 'privacy' || kind === 'account-deletion',
  );
  const heading =
    kind === 'account-deletion'
      ? 'Delete your GarageGymBros account'
      : kind === 'child-safety'
        ? 'Child safety standards'
        : kind === 'privacy'
          ? 'Your privacy matters.'
          : kind === 'terms'
            ? 'Community terms'
            : 'Keep the garage welcoming.';
  return (
    <div className="policy-page">
      <a href="/home">
        <Logo />
      </a>
      <span className="eyebrow">COMMUNITY POLICIES</span>
      <h1>{heading}</h1>
      {kind === 'account-deletion' ? (
        <>
          <p>
            In GarageGymBros, go to Profile → Settings → Privacy &amp; data → Delete account.
            Confirm your identity and submit the deletion request.
          </p>
          <p>
            <NavLink to="/settings?tab=privacy#delete-account">
              Open account deletion settings
            </NavLink>{' '}
            (sign-in required).
          </p>
          <p>
            If you can’t sign in or no longer have the app, email{' '}
            <a href="mailto:admin@garagegymbros.com?subject=GarageGymBros%20account%20deletion">
              admin@garagegymbros.com
            </a>{' '}
            to request deletion. Include your username and use the email associated with your
            account if possible. We’ll verify ownership; don’t send your password.
          </p>
          <p>
            You can cancel a pending request. Marco Lopez completes requests 7–30 days after they
            are submitted and emails you when deletion is complete.
          </p>
          <p>
            {accountDeletionMessagePolicy(erasesMessages)} Shared catalog entries remain available.
            Encrypted backups expire within 35 days. Minimal erasure audit records are retained for
            up to 90 days. See our <NavLink to="/policies/privacy">privacy policy</NavLink> for
            details.
          </p>
        </>
      ) : kind === 'child-safety' ? (
        <>
          <p>
            GarageGymBros is for adults aged 18 and over. Child sexual abuse and exploitation (CSAE)
            are prohibited, including child sexual abuse material (CSAM), grooming, sextortion,
            trafficking, and attempts to solicit or share such material.
          </p>
          <p>
            Use Report on the relevant profile, post, comment, listing, or message to alert
            moderators. You can also contact our child safety team at{' '}
            <a href="mailto:admin@garagegymbros.com?subject=GarageGymBros%20child%20safety">
              admin@garagegymbros.com
            </a>
            . Include a link or the username if available. Do not download, forward, or attach
            abusive material.
          </p>
          <p>
            We review reports, remove prohibited material, take action on the accounts involved, and
            report confirmed CSAM to the National Center for Missing &amp; Exploited Children or the
            relevant regional authority as required.
          </p>
        </>
      ) : kind === 'privacy' ? (
        <>
          <p>
            Members choose their profile, equipment, and content visibility. Purchase amounts,
            receipts, and private notes are private. Shared catalog facts remain independent of
            member purchase history.
          </p>
          <p>
            Supabase stores live account and community data, Cloudflare delivers the website, and
            Resend delivers account emails. Browser storage keeps your web sign-in session. The iOS
            app keeps session tokens in the device-only Keychain. The Android app keeps its session
            in app-private WebView storage, which is excluded from device backup. The Android app
            uses the system photo picker or camera only when you choose to add an image. We do not
            use advertising trackers. Member avatar and gym photo uploads use private processing
            when enabled for the account. Video and 3D uploads remain unavailable.
          </p>
          <p>
            Account exports and deletion requests require an identity check. Deletion requests are
            queued and can be cancelled while pending. Marco Lopez manually completes deletion 7–30
            days after your request and emails you when it is complete. Use Deactivate if you want
            to restrict your profile while a request is pending.{' '}
            {accountDeletionMessagePolicy(erasesMessages)} Shared catalog entries remain available.
          </p>
          <p>
            Active account data is retained to provide the service. Encrypted operational backups
            can retain earlier copies for up to 35 days; restoration must reapply completed deletion
            records. Minimal erasure audit records are retained for up to 90 days. Automated erasure
            is not currently enabled.
          </p>
          <p>
            <NavLink to="/policies/account-deletion">Request account and data deletion</NavLink>
          </p>
        </>
      ) : kind === 'terms' ? (
        <>
          <p>
            GarageGymBros is a community for garage and home gym enthusiasts aged 18 or older. Share
            content you have permission to publish and respect other members’ privacy and visibility
            choices. Features and availability are subject to service limits.
          </p>
          <p>
            Members arrange local equipment trades directly. No platform payments, shipping, escrow,
            buyer protection, verified ownership, or equipment-condition guarantees are provided.
          </p>
          <p>
            Equipment measurements and community reviews are informational. Check manufacturer
            instructions and equipment requirements. Community posts and reviews do not replace
            manufacturer guidance or qualified advice. Report unsafe, misleading, or unlawful
            content through its report action or contact support.
          </p>
        </>
      ) : (
        <>
          <p>
            Be constructive, respect different budgets and spaces, and share first-hand experience
            honestly. Criticism and low ratings are welcome when relevant and respectful.
          </p>
          <p>
            Harassment, hate, threats, scams, sexual exploitation, illegal content, and disclosure
            of private home addresses are prohibited. Disclose brand relationships, gifts, or review
            incentives. Do not imply a purchase has been verified when it has not.
          </p>
          <p>
            Report profiles, content, messages, reviews, or trades using their report action. Block
            members to stop interactions. Moderation decisions require reasons and an audit history;
            appeals can be submitted through support.
          </p>
          <p>
            <NavLink to="/policies/child-safety">Child safety standards and reporting</NavLink>
          </p>
        </>
      )}
      <p>
        Questions, privacy requests, and support:{' '}
        <a href="mailto:admin@garagegymbros.com">admin@garagegymbros.com</a>.
      </p>
      <Button onClick={() => history.back()}>Back to the garage</Button>
    </div>
  );
}
